import { t } from "@lingui/macro";
import { CaretRight, Lock, SidebarSimple } from "@phosphor-icons/react";
import { useBreakpoint } from "@stylingcv/hooks";
import { Button, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { Link } from "react-router-dom";

import { Logo } from "@/client/components/logo";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

// Tablet/Mobile header for builder (Desktop uses BuilderHeader from builder-header.tsx)
export const TabletBuilderHeader = () => {
  const { isMobile, isDesktop } = useBreakpoint();
  const title = useResumeStore((state) => state.resume.title);
  const locked = useResumeStore((state) => state.resume.locked);

  const toggle = useBuilderStore((state) => state.toggle);
  const isDragging = useBuilderStore(
    (state) => state.panel.left.handle.isDragging || state.panel.right.handle.isDragging,
  );
  const leftPanelSize = useBuilderStore((state) => state.panel.left.size);
  const rightPanelSize = useBuilderStore((state) => state.panel.right.size);

  const onToggle = (side: "left" | "right") => {
    toggle(side);
  };

  // Hide on mobile (has own nav) and desktop (uses new BuilderHeader)
  if (isMobile || isDesktop) {
    return null;
  }

  // Tablet header
  return (
    <div
      style={{ left: `${leftPanelSize}%`, right: `${rightPanelSize}%` }}
      className={cn(
        "fixed inset-x-0 top-0 z-[60] h-12 border-b border-gray-100 bg-white backdrop-blur-lg dark:border-gray-800 dark:bg-gray-900",
        !isDragging && "transition-[left,right]",
      )}
    >
      <div className="flex h-full items-center justify-between px-4">
        <Button
          size="icon"
          variant="ghost"
          aria-label={t`Toggle left panel`}
          onClick={() => {
            onToggle("left");
          }}
        >
          <SidebarSimple size={20} aria-hidden="true" />
        </Button>

        <div className="flex items-center gap-2">
          <Link to="/">
            <Logo size={100} className="h-6" />
            </Link>
          <CaretRight size={12} className="text-gray-300 rtl:rotate-180" aria-hidden="true" />
          <span className="max-w-[120px] truncate text-sm font-medium text-gray-700 dark:text-gray-200">
            {title || t`Resume`}
          </span>
          {locked && (
            <Tooltip content={t`This resume is locked, please unlock to make further changes.`}>
              <Lock size={14} className="text-amber-500" />
            </Tooltip>
          )}
        </div>

        <Button
          size="icon"
          variant="ghost"
          aria-label={t`Toggle right panel`}
          onClick={() => {
            onToggle("right");
          }}
        >
          <SidebarSimple size={20} className="-scale-x-100" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
};
